import { type Layout } from 'react-grid-layout';

export type TemplateId = 'auto' | '2x2' | '2x3' | '3x3' | '4x3' | 'spotlight';

export interface TemplateDef {
  id: TemplateId;
  label: string;
  /** Tiles the template is shaped for; extras flow at the bottom at fallback size. */
  capacity: number;
  /** Returns RGL layout items at the `lg` (12-col) breakpoint. */
  build: (tileIds: string[]) => Layout[];
}

/** Fallback when a template doesn't fit all tiles — packs at 6w×5h (~640×360), 2 per row. */
function packExtras(tileIds: string[], offset: number, startY: number): Layout[] {
  const w = 6;
  const h = 5;
  return tileIds.slice(offset).map((id, idx) => ({
    i: id,
    x: (idx % 2) * w,
    y: startY + Math.floor(idx / 2) * h,
    w,
    h,
    minW: 2,
    minH: 2,
  }));
}

function uniformGrid(
  tileIds: string[],
  cols: number,
  rows: number,
  tileW: number,
  tileH: number,
): Layout[] {
  const cap = cols * rows;
  const used = tileIds.slice(0, cap).map((id, idx) => ({
    i: id,
    x: (idx % cols) * tileW,
    y: Math.floor(idx / cols) * tileH,
    w: tileW,
    h: tileH,
    minW: 2,
    minH: 2,
  }));
  const extras = packExtras(tileIds, cap, rows * tileH);
  return [...used, ...extras];
}

export const TEMPLATES: Record<TemplateId, TemplateDef> = {
  auto: {
    id: 'auto',
    label: 'Auto pack',
    capacity: Infinity,
    // First-open default: 2-wide grid, ~640×360 (native LOW-stream pixels).
    // At lg (12 cols, 80px rowHeight): 6w × 5h ≈ 556×400, the closest the grid
    // gets to 640×360 without changing rowHeight (which would resize every
    // saved user layout). Users can drag to resize from there.
    build: (tileIds) =>
      tileIds.map((id, idx) => ({
        i: id,
        x: (idx % 2) * 6,
        y: Math.floor(idx / 2) * 5,
        w: 6,
        h: 5,
        minW: 2,
        minH: 2,
      })),
  },
  '2x2': {
    id: '2x2',
    label: '2 × 2',
    capacity: 4,
    build: (tileIds) => uniformGrid(tileIds, 2, 2, 6, 4),
  },
  '2x3': {
    id: '2x3',
    label: '2 × 3',
    capacity: 6,
    build: (tileIds) => uniformGrid(tileIds, 2, 3, 6, 3),
  },
  '3x3': {
    id: '3x3',
    label: '3 × 3',
    capacity: 9,
    build: (tileIds) => uniformGrid(tileIds, 3, 3, 4, 3),
  },
  '4x3': {
    id: '4x3',
    label: '4 × 3',
    capacity: 12,
    build: (tileIds) => uniformGrid(tileIds, 4, 3, 3, 3),
  },
  spotlight: {
    id: 'spotlight',
    label: '1 + 5 spotlight',
    capacity: 6,
    build: (tileIds) => {
      if (tileIds.length === 0) return [];
      const big = { i: tileIds[0], x: 0, y: 0, w: 8, h: 6, minW: 3, minH: 3 };
      const smalls = tileIds.slice(1, 6).map((id, idx) => {
        // Two tiles down the right column (8..12), three tiles across below the big one.
        if (idx < 2) {
          return { i: id, x: 8, y: idx * 3, w: 4, h: 3, minW: 2, minH: 2 };
        }
        const k = idx - 2;
        return { i: id, x: k * 4, y: 6, w: 4, h: 3, minW: 2, minH: 2 };
      });
      const extras = packExtras(tileIds, 6, 9);
      return [big, ...smalls, ...extras];
    },
  },
};

export const TEMPLATE_ORDER: TemplateId[] = ['auto', '2x2', '2x3', '3x3', '4x3', 'spotlight'];

/** Used by the picker to render the small visual glyph beside each template name. */
export function templateGlyph(id: TemplateId): Array<{ x: number; y: number; w: number; h: number }> {
  // Returns rects in a 12×8 viewbox so the menu can render them as <rect>s.
  const norm = (rects: Layout[], maxX = 12, maxY = 9): Array<{ x: number; y: number; w: number; h: number }> =>
    rects.map((r) => ({ x: r.x / maxX, y: r.y / maxY, w: r.w / maxX, h: r.h / maxY }));
  const sampleIds = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l'];
  switch (id) {
    case 'auto':   return norm(TEMPLATES.auto.build(sampleIds.slice(0, 6)));
    case '2x2':    return norm(TEMPLATES['2x2'].build(sampleIds.slice(0, 4)), 12, 8);
    case '2x3':    return norm(TEMPLATES['2x3'].build(sampleIds.slice(0, 6)));
    case '3x3':    return norm(TEMPLATES['3x3'].build(sampleIds.slice(0, 9)));
    case '4x3':    return norm(TEMPLATES['4x3'].build(sampleIds.slice(0, 12)));
    case 'spotlight': return norm(TEMPLATES.spotlight.build(sampleIds.slice(0, 6)));
  }
}

/** Compare two layouts (ignoring ordering of items). Used by the picker to
 * decide whether applying a template would overwrite a customised layout. */
export function layoutsEqual(a: Layout[] | undefined, b: Layout[]): boolean {
  if (!a || a.length !== b.length) return false;
  const byKey = new Map(a.map((l) => [l.i, l]));
  return b.every((l) => {
    const o = byKey.get(l.i);
    return o && o.x === l.x && o.y === l.y && o.w === l.w && o.h === l.h;
  });
}
